import { differenceInCalendarDays } from 'date-fns';
import { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Typography } from '@/components/ui/typography';
import {
  formatInTimeZone,
  getRangeTimeZoneLabel,
  localDayAt,
} from '@/utils/time-zone';

import { TimeZoneChip, TimeZoneChipZone } from '../time-zone-chip';

export type GoalWindow = {
  start: { date: string; time: string };
  end: { date: string; time: string } | null;
  timeZoneLabel: string;
  totalDays: number | null;
};

/**
 * A hiring goal's start and end at the opening, the zone label for the days they
 * span, and how many of those days the goal runs.
 * @param startDate - The goal's start instant. eg '2026-09-01T05:00:00Z'
 * @param endAt - The instant the goal ends, resolved by the API; null when open-ended.
 * @param timeZone - The opening's IANA zone. eg 'America/Chicago'
 * @returns The window, or null for an unknown zone.
 */
export function getGoalWindow(
  startDate: string,
  endAt: string | null | undefined,
  timeZone: string,
): GoalWindow | null {
  const startAt = new Date(startDate);
  const start = formatInTimeZone(startAt, timeZone);
  const startDay = localDayAt(startAt, timeZone);
  if (!start || !startDay) return null;

  const endInstant = endAt ? new Date(endAt) : null;
  const end = endInstant ? formatInTimeZone(endInstant, timeZone) : null;
  const endDay = endInstant ? localDayAt(endInstant, timeZone) : null;
  // An open-ended goal has no last day, so no one abbreviation holds for all of it.
  const timeZoneLabel = endDay
    ? getRangeTimeZoneLabel(startDay, endDay, timeZone)
    : timeZone;
  if (!timeZoneLabel) return null;

  return {
    start,
    end,
    timeZoneLabel,
    totalDays: endDay ? differenceInCalendarDays(endDay, startDay) + 1 : null,
  };
}

export function GoalStamp({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Typography variant="caption">{label}</Typography>
      <Typography variant="body2" className="whitespace-nowrap">
        {children}
      </Typography>
    </div>
  );
}

export function GoalInstant({ date, time }: { date: string; time: string }) {
  return (
    <>
      {date} <span className="text-fx-neutral-600 font-normal">{time}</span>
    </>
  );
}

/** The chip naming the zone a goal's dates and times are in. */
export function GoalTimeZoneChip({
  timeZone,
  timeZoneLabel,
}: {
  timeZone: string;
  timeZoneLabel: string;
}) {
  return (
    <TimeZoneChip timeZone={timeZone}>
      <FormattedMessage
        defaultMessage="All times in <zone>{timeZone}</zone>"
        description="Chip below the hiring goal's dates. {timeZone} is an IANA zone with its abbreviation, such as America/Chicago (CDT)"
        values={{
          timeZone: timeZoneLabel,
          zone: chunks => <TimeZoneChipZone>{chunks}</TimeZoneChipZone>,
        }}
      />
    </TimeZoneChip>
  );
}

export function GoalStartsLabel() {
  return (
    <FormattedMessage
      defaultMessage="Starts"
      description="Caption above the hiring goal's start date and time"
    />
  );
}

export function GoalEndsLabel() {
  return (
    <FormattedMessage
      defaultMessage="Ends"
      description="Caption above the hiring goal's end date and time"
    />
  );
}
